import type { ZeroXWithdrawalState } from '../schemas/zeroXWithdrawal';
import { PAYMENT_METHOD } from '../types/enums';
import { type CasinoTransaction, type PaymentMethod } from '../types/transactions';

const CRYPTO_PAYMENT_METHODS: PaymentMethod[] = [
  PAYMENT_METHOD.CRYPTO,
  PAYMENT_METHOD.USDT_BEP20,
  PAYMENT_METHOD.USDT_TRC20,
];

/** Short labels for history; the cashier form shows the longer explanations. */
export const WITHDRAWAL_STATE_LABEL_KEYS: Record<ZeroXWithdrawalState, string> = {
  awaiting_approval: 'zeroXStateAwaitingApproval',
  submitting: 'zeroXStateProcessing',
  pending: 'zeroXStateProcessing',
  unknown: 'zeroXStateUnderReview',
  review: 'zeroXStateUnderReview',
  confirmed: 'zeroXStateCompleted',
  aborted: 'zeroXStateFailed',
  rejected: 'zeroXStateRejected',
};

/** Explanations shown in the cashier after a confirmation. */
export const WITHDRAWAL_STATE_MESSAGE_KEYS: Record<ZeroXWithdrawalState, string> = {
  awaiting_approval: 'zeroXWithdrawalAwaitingApproval',
  submitting: 'zeroXWithdrawalSubmitting',
  pending: 'zeroXWithdrawalPending',
  unknown: 'zeroXWithdrawalUnknown',
  review: 'zeroXWithdrawalUnknown',
  confirmed: 'zeroXWithdrawalConfirmed',
  aborted: 'zeroXWithdrawalAborted',
  rejected: 'zeroXWithdrawalRejected',
};

export const isCryptoPaymentMethod = (paymentMethod: PaymentMethod) => CRYPTO_PAYMENT_METHODS.includes(paymentMethod);

export const getCryptoAssetAmount = (transaction: CasinoTransaction) => {
  if (!isCryptoPaymentMethod(transaction.paymentMethod)) {
    return null;
  }

  const withdrawal = transaction.metadata?.cryptoWithdrawal;

  if (withdrawal) {
    return { value: withdrawal.assetAmount, unit: withdrawal.assetCurrency };
  }

  const crypto = transaction.metadata?.crypto;

  if (crypto?.grossAssetAmount && crypto.assetCurrency) {
    return { value: crypto.grossAssetAmount, unit: crypto.assetCurrency };
  }

  const usdt = transaction.metadata?.usdtAmount;

  return typeof usdt === 'number' && Number.isFinite(usdt) ? { value: usdt.toFixed(2), unit: 'USDT' } : null;
};

export const getCryptoAssetLabel = (transaction: CasinoTransaction) => {
  const amount = getCryptoAssetAmount(transaction);

  return amount ? `${amount.value} ${amount.unit}` : null;
};

export const getCryptoProviderLabel = (transaction: CasinoTransaction) =>
  isCryptoPaymentMethod(transaction.paymentMethod) &&
  (transaction.metadata?.crypto?.provider === 'zero_x_processing' ||
    transaction.metadata?.cryptoWithdrawal?.provider === 'zero_x_processing')
    ? '0xProcessing'
    : null;

/** One status rule for every history view: a 0x withdrawal reports its own state. */
export const getTransactionStatusKey = (transaction: CasinoTransaction) => {
  const state = transaction.metadata?.cryptoWithdrawal?.state;
  return state ? WITHDRAWAL_STATE_LABEL_KEYS[state] : transaction.status;
};

/** Fee column: the provider's actual fee once known, otherwise the estimate; unknown stays empty. */
export const getTransactionFee = (transaction: CasinoTransaction) => {
  const withdrawal = transaction.metadata?.cryptoWithdrawal;
  if (!withdrawal) return null;
  if (withdrawal.actualFee != null)
    return { value: `${withdrawal.actualFee} ${withdrawal.assetCurrency}`, estimated: false };
  return { value: `${withdrawal.estimatedNetworkFee} ${withdrawal.assetCurrency}`, estimated: true };
};
